import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { TripAttributesGps } from '../dto/visit-gps.interface';

/**
 * Inactivity Cell Renderer
 *
 * Muestra el tiempo de inactividad del vehículo y cambia color según nivel de alerta.
 *
 * @example
 * ```typescript
 * {
 *   field: 'inactivity',
 *   headerName: 'Inactividad',
 *   cellRenderer: InactivityCellRendererComponent,
 *   minWidth: 180
 * }
 * ```
 */
@Component({
  selector: 'app-inactivity-cell-renderer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      class="text-center font-semibold rounded px-2 py-1 w-full"
      [ngClass]="getColorClass(params.data)"
    >
      {{ getTimeInactive(params.data) }}
    </div>
  `,
})
export class InactivityCellRendererComponent implements CellRenderer<TripAttributesGps> {
  @Input() params!: CellRendererParams<TripAttributesGps>;

  /**
   * Determina la clase de color según los minutos de inactividad.
   */
  getColorClass(element: TripAttributesGps): string {
    if (!element) return 'text-gray-400';
    const minutes = element.alertInactiveMinutes ?? 0;

    if (minutes > 10) {
      return 'text-red-600';
    } else if (minutes > 5) {
      return 'text-yellow-500';
    } else if (minutes >= 0) {
      return 'text-green-500';
    } else {
      return 'text-gray-400';
    }
  }

  /**
   * Calcula el tiempo de inactividad en formato "HHh:MMm:SSs"
   * usando objetos Date nativos.
   */
  getTimeInactive(item: TripAttributesGps): string {
    const gps = item?.locations?.[0];
    if (!gps?.timestamp) {
      item.alertInactiveMinutes = null;
      return '--:--';
    }

    // Obtener fechas
    const now = new Date();
    const timestamp = new Date(gps.timestamp);

    // Calcular diferencia en segundos
    const diffMs = now.getTime() - timestamp.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);

    // Guardar minutos para el color
    item.alertInactiveMinutes = diffMinutes;

    // Convertir a HH:MM:SS
    const hours = Math.floor(diffSeconds / 3600);
    const minutes = Math.floor((diffSeconds % 3600) / 60);
    const seconds = diffSeconds % 60;

    const rhour = hours.toString().padStart(2, '0');
    const rminute = minutes.toString().padStart(2, '0');
    const rsecond = seconds.toString().padStart(2, '0');

    return `${rhour}h:${rminute}m:${rsecond}s`;
  }
}
